import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { confirmSignUp, resendSignUpCode, type ConfirmSignUpInput } from 'aws-amplify/auth';

const VerificationForm: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const email = location.state?.email;
    const [confirmationCode, setConfirmationCode] = useState('');

    useEffect(() => {
        if (!email) {
            navigate('/login');
        }
    }, [email, navigate]);

    const handleSignUpConfirmation = async ({ username, confirmationCode }: ConfirmSignUpInput) => {
        if (!username) {
            navigate('/login');
        }
        try {
            const { isSignUpComplete, nextStep } = await confirmSignUp({ username, confirmationCode });
            if (isSignUpComplete) {
                navigate('/login', { state: { registrationSuccess: true } });
            } else {
                console.log('Sign-up confirmation not complete. Next step:', nextStep);
            }
        } catch (error) {
            if (error instanceof Error) {
                switch ((error as any).name) {
                    case "EmptyConfirmSignUpUsername":
                        navigate('/login');
                        break;
                }
            }
            console.error('Error confirming sign-up:', error);
        }
    };

    const handleResendMfaToken = async (e: React.MouseEvent<HTMLButtonElement>) => {
        try {
            await resendSignUpCode({ username: email });
            console.log("MFA token resent successfully");
            // Optional: Show a success message to the user
        } catch (error) {
            console.error("Error resending MFA token:", error);
            // Optional: Show an error message to the user
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        await handleSignUpConfirmation({ username: email, confirmationCode });
    };

    const isSubmitDisabled = confirmationCode.trim() === '';

    return (
        <div className="min-h-screen bg-gray-100 flex justify-center items-center">
            <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
                <div className="flex justify-center">
                    <img
                        src="/images/jack-logo.jpg"
                        alt="Jack Logo"
                        style={{ maxWidth: '80%', maxHeight: '30%' }}
                        className="pb-8"
                    />
                </div>
                <h2 className="text-2xl font-bold mb-6 text-center">Verify Your Account</h2>
                <p className="text-gray-600 mb-6">A verification code has been sent to your phone number.</p>
                <form onSubmit={handleSubmit}>
                    <div className="mb-6">
                        <label htmlFor="confirmationCode" className="block mb-2 font-bold text-gray-700">
                            Verification Code
                        </label>
                        <input
                            type="text"
                            id="confirmationCode"
                            autoComplete='one-time-code'
                            placeholder="Enter the verification code"
                            value={confirmationCode}
                            onChange={(e) => setConfirmationCode(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        />
                    </div>
                    <button
                        type="submit"
                        className={`w-full text-white font-bold py-2 px-4 rounded-md transition duration-300 ${isSubmitDisabled
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-[#43a4db] hover:bg-[#3786b4]'
                            }`}
                        disabled={isSubmitDisabled}
                    >
                        Verify
                    </button>
                </form>
                <div className="mt-4 text-center">
                    <span className="text-gray-600">Didn’t receive a verification code? </span>
                    <button
                        type="button"
                        onClick={handleResendMfaToken}
                        className="text-blue-600 hover:underline"
                    >
                        Resend it now
                    </button>
                </div>
            </div>
        </div>
    );
};

export default VerificationForm;